/**
 * Module Configuration
 * Defines which modules are enabled for which industries
 *
 * NOTE: This is a legacy compatibility layer. The authoritative module configs
 * are in ./modules/index.ts with full industry-specific overrides.
 */

export type ModuleId =
  | 'command-center'
  | 'asset-intelligence'
  | 'predictive-ops'
  | 'compare'
  | 'root-cause'
  | 'maintenance'
  | 'self-learning'
  | 'executive'

// All canonical industries (20+). 2026-07-02: aquaculture_lobster,
// biosensor_ide_wearable and wearable_etd added — this matrix was stuck at
// 18 while the backend registries went canonical-20, which silently
// DISABLED all 8 core modules (sidebar + IndustryRoute) for the new
// verticals. Any new industry MUST be added here or its UI is gutted.
export type IndustryId =
  | 'manufacturing'
  | 'oil_gas'
  | 'healthcare'
  | 'financial'
  | 'energy'
  | 'transportation'
  | 'mining'
  | 'chemicals'
  | 'food_beverage'
  | 'pharmaceuticals'
  | 'aerospace'
  | 'metals'
  | 'iot_telecom'
  | 'utilities'
  | 'pulp_paper'
  | 'automotive'
  | 'datacenter'
  | 'facility_management'
  | 'aquaculture_lobster'
  | 'biosensor_ide_wearable'
  | 'wearable_etd'
  | 'wind'
  | 'defence'
  | 'nuclear'

// All canonical industries for enabled modules
const ALL_INDUSTRIES: IndustryId[] = [
  'manufacturing', 'oil_gas', 'healthcare', 'financial', 'energy', 'transportation',
  'mining', 'chemicals', 'food_beverage', 'pharmaceuticals', 'aerospace', 'metals',
  'iot_telecom', 'utilities', 'pulp_paper', 'automotive', 'datacenter', 'facility_management',
  'aquaculture_lobster', 'biosensor_ide_wearable', 'wearable_etd',
  'wind', 'defence', 'nuclear', // 2026-07-14 — cards/data/re-key live (migs 228/232); un-disable the 8 modules
]

// Industries that have a Maintenance flow.
// v99:  healthcare enabled with a clinical-engineering variant (BMET PM,
//       calibration, TJC EC.02.04.03) — patient-centric strategic POV
//       preserved via patient-impact framing on every card.
// v100: financial enabled with an IT-change-mgmt variant (change
//       windows, deployment freezes, runbook reviews, SOX / PCI-DSS /
//       Reg-SCI change controls). "Maintenance" reads as Site
//       Reliability / Change Management for the SRE persona.
// All 24 industries now have Maintenance.
const MAINTENANCE_INDUSTRIES: IndustryId[] = [
  'manufacturing', 'oil_gas', 'energy', 'transportation', 'mining', 'chemicals',
  'food_beverage', 'pharmaceuticals', 'aerospace', 'metals', 'iot_telecom',
  'utilities', 'pulp_paper', 'automotive', 'datacenter', 'facility_management',
  'healthcare', // v99 — clinical-engineering variant
  'financial',  // v100 — IT-change-mgmt variant
  'aquaculture_lobster',    // 2026-07-02 — cage/pump/feeder maintenance (MaintenanceAquaculture)
  'biosensor_ide_wearable', // 2026-07-02 — device-fleet maintenance (MaintenanceBiosensor)
  'wearable_etd',           // 2026-07-02 — ETD detector calibration + swab-reader servicing
  'wind',                   // 2026-07-14 — turbine gearbox/blade/pitch PM
  'defence',                // 2026-07-14 — MANUAL-tier floor, but PM flow enabled
  'nuclear',                // 2026-07-14 — MANUAL-tier floor, but PM flow enabled
]

/**
 * Module enablement by industry
 *
 * Maintenance Intelligence is now enabled for ALL 24 industries:
 *   - 16 rotating/static-equipment industries (config-driven Industrial)
 *   - iot_telecom (bespoke connectivity-policy maintenance)
 *   - healthcare (bespoke clinical-engineering — v99)
 *   - financial (bespoke IT-change-mgmt — v100)
 */
const moduleIndustryMatrix: Record<ModuleId, IndustryId[]> = {
  'command-center': ALL_INDUSTRIES,
  'asset-intelligence': ALL_INDUSTRIES,
  'predictive-ops': ALL_INDUSTRIES,
  'compare': ALL_INDUSTRIES,
  'root-cause': ALL_INDUSTRIES,
  'maintenance': MAINTENANCE_INDUSTRIES, // v99: includes healthcare; financial still pending v100
  'self-learning': ALL_INDUSTRIES,
  'executive': ALL_INDUSTRIES,
}

/**
 * Check if a module is enabled for a given industry
 */
export function isModuleEnabled(moduleId: ModuleId, industryId: string): boolean {
  const enabledIndustries = moduleIndustryMatrix[moduleId]
  if (!enabledIndustries) {
    return true // Default to enabled if not in matrix
  }
  return enabledIndustries.includes(industryId as IndustryId)
}

/**
 * Get all enabled modules for an industry
 */
export function getEnabledModules(industryId: string): ModuleId[] {
  return Object.entries(moduleIndustryMatrix)
    .filter(([_, industries]) => industries.includes(industryId as IndustryId))
    .map(([moduleId, _]) => moduleId as ModuleId)
}
